const MP4_DIRECTORY = '/video_creation/mp4/';

// Newest first. Add a version to retire every rendered file at once: the existence probe misses it,
// so each project re-renders from its current content on its next share.
export const RENDER_VERSIONS = ['v2'];

// Must match the sanitising VideoTemplateController::createVideo() applies before writing the file.
export const sanitizeVideoFileName = (name) => String(name ?? '').replace(/[ /.]/g, '_');

function instanceBaseName({ projectId = null, templateId = null, name = '' }) {
  const prefix = projectId ? `p${projectId}_` : (templateId ? `t${templateId}_` : '');

  return prefix + sanitizeVideoFileName(name);
}

// sanitizeVideoFileName() only strips space, '/' and '.' — anything else a brief title can carry
// (|, :, ?, #, &, the "…" truncateLabel appends when it cuts a name) survives into the filename
// and, left raw, breaks Laravel's `url` validation rule on file_video_url once the string is
// submitted as a form field instead of navigated to as a URL (POR-13874).
//
// This is the one place that does that encoding, because file_video_url can be produced two
// ways that both need to land on the same spelling: mp4Url() below (reusing an already-rendered
// file found by the existence probe) and VideoTable.vue's freshly-rendered-file branch, which
// builds its URL from the server's response instead of from a local baseName. That second input
// can already be percent-encoded depending on how the server built it, so decode before
// re-encoding rather than encoding blind — otherwise an already-escaped '%7C' would double-encode
// into '%257C' instead of staying '%7C'. A basename that isn't valid percent-encoding (a literal
// '%' in the name, say) fails decodeURIComponent; treat it as already-raw in that case.
//
// Encoding only the basename — never the directory or origin — is safe here because every caller
// guarantees the basename has no '/' left in it to protect (the sanitiser above, or the PHP
// mirror's identical str_replace). It also matches what a browser does silently to a URL passed
// to fetch/axios.head, so the existence probe and the shared URL agree on the same encoded
// spelling.
//
// Do NOT make VideoProject::renderedVideoPublicPath() (PHP mirror) encode to "match" this: that
// helper builds a filesystem path for file_exists(), and the file on disk genuinely has the raw
// character in its name — encoding it there would break the lookup, not fix anything.
export function encodeMp4Basename(pathOrUrl) {
  const separatorIndex = pathOrUrl.lastIndexOf('/');
  const directory = pathOrUrl.slice(0, separatorIndex + 1);
  const basename = pathOrUrl.slice(separatorIndex + 1);

  let decodedBasename;
  try {
    decodedBasename = decodeURIComponent(basename);
  } catch (error) {
    decodedBasename = basename;
  }

  return directory + encodeURIComponent(decodedBasename);
}

function mp4Url(baseName) {
  return encodeMp4Basename(window.location.origin + MP4_DIRECTORY + baseName + '.mp4');
}

export function videoOutputBaseName(instance) {
  return `${RENDER_VERSIONS[0]}_${instanceBaseName(instance)}`;
}

export function videoOutputUrl(instance) {
  return mp4Url(videoOutputBaseName(instance));
}

// Newest naming first, so proofs shared before a rename still play. The last one is the pre-id
// global name, which a same-named project in another company can also own — kept because dropping
// it would blank every pre-fix proof, and proofs made since carry an exact file_video_url instead.
// Mirrored by VideoProject::renderedVideoPublicPath().
export function videoOutputUrlCandidates(instance) {
  const baseName = instanceBaseName(instance);

  return [
    ...RENDER_VERSIONS.map(version => mp4Url(`${version}_${baseName}`)),
    mp4Url(baseName),
    mp4Url(sanitizeVideoFileName(instance.name)),
  ];
}
